// Shared helpers for the month grid and the time-slot list in SlotPicker.

import { getZoneCode } from '../../utils/formatters';

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const DAY_HEADERS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

/** Return the day numbers of a month, with a null for each empty cell before day 1. */
export function getMonthDays(year: number, month: number): (number | null)[] {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  return days;
}

/** Return a "YYYY-MM-DD" key for a day. The month is zero-based. */
export function formatDateKey(
  year: number,
  month: number,
  day: number
): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Return a "YYYY-MM" key for a month. The month is zero-based. */
export function formatMonthKey(year: number, month: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

/** Return the "YYYY-MM-DD" key of an instant in the given timezone. */
export function dateKeyInTimezone(isoTime: string, tz: string): string {
  return new Date(isoTime).toLocaleDateString('en-CA', { timeZone: tz });
}

export function formatTimeSlot(isoTime: string, tz: string): string {
  return new Date(isoTime).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  });
}

export function formatLongDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

/** Format "YYYY-MM-DD" as "Wed, Sep 30", for a narrow column heading. */
export function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** The longest window that the availability APIs accept: 7 days of 24 hours. */
export const MAX_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export interface AvailabilityWindow {
  start: Date;
  end: Date;
}

/**
 * Split the range from `start` to `end` into windows of 168 hours or less.
 * Each window ends where the next one starts, and the last one ends at `end`.
 *
 * The step is a fixed number of milliseconds, not 7 calendar days. A step of
 * 7 calendar days is 169 hours across a clock change, and the APIs reject it.
 */
export function buildAvailabilityWindows(
  start: Date,
  end: Date
): AvailabilityWindow[] {
  const windows: AvailabilityWindow[] = [];
  let windowStart = start.getTime();
  const endTime = end.getTime();
  while (windowStart < endTime) {
    const windowEnd = Math.min(windowStart + MAX_WINDOW_MS, endTime);
    windows.push({ start: new Date(windowStart), end: new Date(windowEnd) });
    windowStart = windowEnd;
  }
  return windows;
}

/**
 * MED-1365 (QA F1): the window to fetch again after a failure. Calendly
 * refuses a start in the past, so a start that is now in the past moves to
 * now. Null when the whole window has ended.
 */
export function freshWindow(
  window: AvailabilityWindow,
  now: Date = new Date()
): AvailabilityWindow | null {
  if (window.start.getTime() >= now.getTime()) return window;
  if (window.end.getTime() <= now.getTime()) return null;
  return { start: now, end: window.end };
}

/**
 * The short timezone name for the instant `at`, for example "ET". The public
 * booking page and the portal confirmation use it. MED-1389 (QA round 3): the
 * same name as every other screen (getZoneCode), never "EDT".
 */
export function getTimezoneShortName(tz: string, at: Date): string {
  return getZoneCode(tz, at);
}
